import { ArrowSquareOutIcon, InfoIcon, QuestionIcon, TrashSimpleIcon, XCircleIcon } from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import invariant from 'tiny-invariant'
import { match, P } from 'ts-pattern'
import PlayButton from '../assets/play-button.svg?react'
import { useGetScanCancellation } from '../hooks/scan-hooks.ts'
import { useNewerScanBanner } from '../hooks/use-newer-scan-banner.ts'
import { Routes } from '../routes.ts'
import { useGetAnalyses, useGetScans } from '../utils/api-client/api-hooks.ts'
import type { ScanResponse } from '../utils/api-client/api-schemas.ts'
import { formatDate, formatDuration } from '../utils/date-utils.ts'
import { isScaTool } from '../utils/detector-utils.ts'
import { getScanInstanceName, getToolName } from '../utils/repository-utils.tsx'
import * as analysisLogStyles from './analysis-log-tooltip.css.ts'
import { ScaBadge } from './badges/sca-badge.tsx'
import { ScanStatusBadge } from './badges/scan-status-badge.tsx'
import { GhostButton } from './buttons/ghost-button.tsx'
import { CancelAnalysisDialog } from './cancel-analysis-dialog.tsx'
import { getQuickViewTotalFindings } from './composition-bar/composition-utils'
import { DeleteScanDialog } from './delete-scan-dialog.tsx'
import { RepositoryHeader } from './repository-header.tsx'
import {
  CompositionBarsContent,
  QuickViewAnalysisMetrics,
  useScanDetailsHeaderBase,
  type ScanDetailsHeaderPureProps,
} from './scan-details-header-shared.tsx'
import * as styles from './scan-details-header.css.ts'
import { Spinner } from './spinner.tsx'
import { BranchTag } from './tags/branch-tag.tsx'
import { ToolIcon } from './third-party-branding/tool-icon.tsx'
import { TooltipWithHover } from './tooltip.tsx'
export {
  CompositionBarsContent,
  QuickViewAnalysisMetrics,
  useScanDetailsHeaderBase,
  type ScanDetailsHeaderPureProps,
} from './scan-details-header-shared.tsx'

// #region Wrapper

export type ScanDetailsHeaderProps = {
  scanId: string
  deleteSuccessRedirectPath?: string
}

export const ScanDetailsHeader: React.FC<ScanDetailsHeaderProps> = ({ scanId, deleteSuccessRedirectPath }) => {
  const props = useScanDetailsHeaderBase({ scanId })
  const navigate = useNavigate()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const handleDeleteScan = () => setIsDeleteDialogOpen(true)
  const handleCancelScan = () => setIsCancelDialogOpen(true)
  const handleDeleteSuccess = useCallback(() => {
    setIsDeleteDialogOpen(false)
    if (deleteSuccessRedirectPath) {
      navigate(deleteSuccessRedirectPath)
    }
  }, [navigate, deleteSuccessRedirectPath])
  const handleCancelSuccess = useCallback(() => {
    setIsCancelDialogOpen(false)
  }, [])

  const repositoryId = props.repository?.id
  const tool = props.scan?.detector
  const newerScan = useNewerScanBanner({ currentScanId: scanId, repositoryId, tool })
  const { data: latestScans, isSuccess: isLatestScansLoaded } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: repositoryId ? [repositoryId] : undefined,
    tools: tool ? [tool] : undefined,
    enabled: !!repositoryId && !!tool,
  })
  const isStale = newerScan?.state === 'has-analysis'
  const isLatest = isLatestScansLoaded && latestScans?._embedded?.items?.[0]?.id === scanId
  const onViewLatest = newerScan
    ? () => navigate(Routes.ScanDetailsPage.createPath({ scanId: newerScan.newerScanId }))
    : undefined

  return (
    <>
      <ScanDetailsHeaderPure
        {...props}
        onDeleteScan={deleteSuccessRedirectPath ? handleDeleteScan : undefined}
        onCancelScan={handleCancelScan}
        isStale={isStale}
        isLatest={isLatest}
        onViewLatest={onViewLatest}
      />
      {isDeleteDialogOpen && (
        <DeleteScanDialog
          scanId={scanId}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
      {isCancelDialogOpen && (
        <CancelAnalysisDialog
          scanId={scanId}
          onClose={() => setIsCancelDialogOpen(false)}
          onCancelSuccess={handleCancelSuccess}
        />
      )}
    </>
  )
}

// #endregion

// #region Pure Component

export const ScanDetailsHeaderPure: React.FC<ScanDetailsHeaderPureProps> = ({
  scan,
  isScanLoading,
  isScanError,
  repository,
  isRepositoryLoading,
  isRepositoryError,
  scanDetailsHeaderState,
  isScanDetailsHeaderStateLoading,
  isScanDetailsHeaderStateError,
  onAnalyzeScan,
  onDeleteScan,
  onCancelScan,
  isScanInputsExpired,
  quickViewCompositionBarsProps,
  isStale,
  isLatest,
  onViewLatest,
  onSkipToInProgressAnalysis,
  hasCancellation,
}) => {
  if (isScanLoading || isRepositoryLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner size="64" label="Loading scan details and findings." />
      </div>
    )
  }

  if (isScanError || isRepositoryError) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <span className={typography.small} style={{ color: mappedColors.neutral.neutral10 }}>
          Error loading scan details. Please refresh the page to try again.
        </span>
      </div>
    )
  }

  if (!scan || !repository) {
    return <div>No scan or repository details found. Does the scan exist?</div>
  }

  const { isFilteredDataTransitioning, quickViewId, filteredStatistics, activeFilterStatistics } =
    quickViewCompositionBarsProps

  const isSca = isScaTool(scan.detector)
  const noAnalyzedFindingsInView =
    !!activeFilterStatistics && getQuickViewTotalFindings(activeFilterStatistics, { isSca, quickViewId }) === 0

  return (
    <header role="banner">
      <ScanDetailsHeaderLayout
        toolIconSlot={<ToolIcon tool={scan.detector} size="large+" withAriaLabel={true} />}
        scanStatusBadgeSlot={
          isStale ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <button className={styles.viewLatestLink} onClick={onViewLatest}>
                View latest
              </button>
              <ScanStatusBadge variant="stale" />
            </div>
          ) : isLatest ? (
            <ScanStatusBadge variant="latest" />
          ) : null
        }
        scanDetailsSlot={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ScanDateTime scan={scan} />
            <BranchTag branch={scan.branch} />
            <ToolLink scan={scan} />
          </div>
        }
        repositoryDetailsSlot={<RepositoryHeader repository={repository} showDefaultBranchTag={false} />}
        compositionBarSlot={
          <CompositionBarsContent
            scanDetailsHeaderState={scanDetailsHeaderState}
            isScanDetailsHeaderStateLoading={isScanDetailsHeaderStateLoading}
            isScanDetailsHeaderStateError={isScanDetailsHeaderStateError}
            detector={scan.detector}
            quickViewCompositionBarsProps={quickViewCompositionBarsProps}
            onSkipToInProgressAnalysis={onSkipToInProgressAnalysis}
            hasCancellation={hasCancellation}
          />
        }
        statusBadgeSlot={match(scanDetailsHeaderState)
          .with(undefined, () => (isScaTool(scan.detector) ? <ScaBadge /> : null))
          .with({ type: 'initial-unanalyzed-scan' }, () => (
            <>
              {onDeleteScan && (
                <TooltipWithHover
                  trigger={
                    <button className={styles.deleteButton} onClick={onDeleteScan} aria-label="Delete scan">
                      <TrashSimpleIcon size={24} weight="light" />
                    </button>
                  }
                  size="small"
                >
                  Delete scan
                </TooltipWithHover>
              )}
              {isScaTool(scan.detector) && <ScaBadge />}
              <ScanStatusBadge variant="unanalyzed" />
            </>
          ))
          .with({ type: 'first-analysis-batch-in-progress' }, () => (
            <>
              {onCancelScan && (
                <TooltipWithHover
                  trigger={
                    <button className={styles.cancelButton} onClick={onCancelScan} aria-label="Cancel analysis">
                      <XCircleIcon size={24} weight="light" />
                    </button>
                  }
                  size="small"
                >
                  Cancel analysis
                </TooltipWithHover>
              )}
              {isScaTool(scan.detector) && <ScaBadge />}
              <ScanStatusBadge variant="in-progress" />
            </>
          ))
          .with({ type: 'findings-with-analysis-complete' }, ({ statistics }) => (
            <>
              {onDeleteScan && (
                <TooltipWithHover
                  trigger={
                    <button className={styles.deleteButton} onClick={onDeleteScan} aria-label="Delete scan">
                      <TrashSimpleIcon size={24} weight="light" />
                    </button>
                  }
                  size="small"
                >
                  Delete scan
                </TooltipWithHover>
              )}
              {isScaTool(scan.detector) && <ScaBadge />}
              <SonarApiWarning detector={scan.detector} totalFindings={statistics.total} />
              {isScanInputsExpired && <ScanInputsExpiredWarning />}
            </>
          ))
          .with({ type: 'findings-with-analysis-complete-and-in-progress' }, ({ statistics }) => (
            <>
              {onDeleteScan && (
                <TooltipWithHover
                  trigger={
                    <button className={styles.deleteButton} onClick={onDeleteScan} aria-label="Delete scan">
                      <TrashSimpleIcon size={24} weight="light" />
                    </button>
                  }
                  size="small"
                >
                  Delete scan
                </TooltipWithHover>
              )}
              {isScaTool(scan.detector) && <ScaBadge />}
              <SonarApiWarning detector={scan.detector} totalFindings={statistics.total} />
              {isScanInputsExpired && <ScanInputsExpiredWarning />}
              {statistics.in_progress_analysis > 0 &&
                (onCancelScan ? (
                  <TooltipWithHover
                    trigger={
                      <button
                        type="button"
                        className={styles.updatingCancelButton}
                        onClick={onCancelScan}
                        aria-label="Cancel analysis"
                      >
                        <span className={styles.updatingIconSlot}>
                          <Spinner size="24" variant="updating" label="Updating findings" />
                        </span>
                        <span className={styles.updatingCancelIconSlot} aria-hidden="true">
                          <XCircleIcon size={24} weight="light" />
                        </span>
                        <span className={styles.updatingTextSmall}>{statistics.in_progress_analysis} updating</span>
                      </button>
                    }
                    size="small"
                  >
                    Cancel analysis
                  </TooltipWithHover>
                ) : (
                  <div className={styles.updatingContainer}>
                    <Spinner size="24" variant="updating" label="Updating findings" />
                    <span className={styles.updatingTextSmall}>{statistics.in_progress_analysis} updating</span>
                  </div>
                ))}
            </>
          ))
          .exhaustive()}
        hideMetrics={noAnalyzedFindingsInView && scanDetailsHeaderState?.type !== 'initial-unanalyzed-scan'}
        metricsSlot={match(scanDetailsHeaderState)
          .with(undefined, () => null)
          .with({ type: 'initial-unanalyzed-scan' }, () => (
            <GhostButton size="small" className={styles.analyzeButton} onClick={onAnalyzeScan}>
              <PlayButton style={{ color: mappedColors.neutral.halo }} />
              <span>Analyze all</span>
            </GhostButton>
          ))
          .with({ type: 'first-analysis-batch-in-progress' }, () => null)
          .with(
            { type: P.union('findings-with-analysis-complete', 'findings-with-analysis-complete-and-in-progress') },
            ({ statistics: globalStats }) => (
              <QuickViewAnalysisMetrics
                tool={scan.detector}
                quickViewId={quickViewId}
                filteredStatistics={
                  noAnalyzedFindingsInView ? filteredStatistics : (activeFilterStatistics ?? filteredStatistics)
                }
                globalStats={globalStats}
                isFilteredDataTransitioning={isFilteredDataTransitioning}
                hasActiveFilters={!!activeFilterStatistics}
              />
            )
          )
          .exhaustive()}
      />
    </header>
  )
}

// #endregion

// #region Sub-components

type ScanDetailsHeaderLayoutProps = {
  toolIconSlot: React.ReactNode
  scanDetailsSlot: React.ReactNode
  repositoryDetailsSlot: React.ReactNode
  compositionBarSlot: React.ReactNode
  scanStatusBadgeSlot?: React.ReactNode
  statusBadgeSlot: React.ReactNode
  metricsSlot: React.ReactNode
  hideMetrics?: boolean
}

export const ScanDetailsHeaderLayout: React.FC<ScanDetailsHeaderLayoutProps> = ({
  toolIconSlot,
  scanDetailsSlot,
  repositoryDetailsSlot,
  compositionBarSlot,
  scanStatusBadgeSlot,
  statusBadgeSlot,
  metricsSlot,
  hideMetrics,
}) => (
  <div className={styles.headerGrid}>
    <div className={styles.gridCellRow1Column1}>{toolIconSlot}</div>
    <div className={styles.gridCellRow2Column1}>{scanDetailsSlot}</div>
    <div className={styles.gridCellRow1Column2}>{repositoryDetailsSlot}</div>
    <div className={styles.gridCellRow2Column2}>{compositionBarSlot}</div>
    <div className={styles.gridCellRow1Column3}>
      {statusBadgeSlot}
      {scanStatusBadgeSlot}
    </div>
    <div className={styles.gridCellRow2Column3} style={hideMetrics ? { visibility: 'hidden' } : undefined}>
      {metricsSlot}
    </div>
  </div>
)

type ToolLinkProps = {
  scan: Pick<ScanResponse, 'detector' | 'html_url'>
}

const ToolLink: React.FC<ToolLinkProps> = ({ scan }) => {
  const toolName = getToolName(scan.detector)
  const toolUrl = scan.html_url

  if (!toolUrl) {
    return null
  }

  return (
    <a href={toolUrl} target="_blank" rel="nopener noreferrer" className={styles.toolLink}>
      View in {toolName}
      <ArrowSquareOutIcon className={styles.externalLinkIcon} size={12} weight="light" />
    </a>
  )
}

type ScanDateTimeProps = {
  scan: Pick<ScanResponse, 'imported_at' | 'id' | 'detector' | '_embedded'>
}

const ScanDateTime: React.FC<ScanDateTimeProps> = ({ scan }) => {
  invariant(scan.imported_at, 'Scan is missing imported_at')

  return (
    <div className={styles.dateTimeContainer}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
        <p className={styles.dateText}>{formatDate({ date: scan.imported_at, type: 'mm/dd/yy' })}</p>
        {/* One tooltip on the page, so there is no per-row cost to defer: load it up front and have
            it populated before the pointer arrives. */}
        <ScanDateTimeTooltip {...toScanDateTimeTooltipProps(scan)} shouldLoadEagerly />
      </div>
      <p className={styles.timeText}>{formatDate({ date: scan.imported_at, type: 'time-with-zone' })}</p>
    </div>
  )
}

type ScanDateTimeTooltipProps = {
  /** The id this component queries with. Presentation values come in as props, not as a whole scan. */
  scanId: string
  importedAt: string
  toolName?: string
  instanceName?: string
  open?: boolean
  /**
   * Loads the analysis lookups on mount instead of on first reveal. Scan lists render one tooltip per
   * row and would otherwise fire a pair of requests per row, so they leave this off; a surface with a
   * single tooltip turns it on and trades nothing for a tooltip that is ready when it opens.
   */
  shouldLoadEagerly?: boolean
}

/**
 * The tooltip's props for a given scan. Exported so the surfaces that render one derive them the
 * same way — `detector` rather than `_embedded.integration.type` via `getIntegrationName`, because
 * the detector is the more specific of the two ("GitLab SAST" vs "GitLab") and naming the producer
 * usefully is the tooltip's whole job.
 */
export const toScanDateTimeTooltipProps = (
  scan: Pick<ScanResponse, 'id' | 'imported_at' | 'detector' | '_embedded'>
) => ({
  scanId: scan.id,
  importedAt: scan.imported_at,
  toolName: getToolName(scan.detector),
  instanceName: getScanInstanceName(scan),
})

export const ScanDateTimeTooltip: React.FC<ScanDateTimeTooltipProps> = ({
  scanId,
  importedAt,
  toolName,
  instanceName,
  open,
  shouldLoadEagerly = false,
}) => {
  const [hasBeenShown, setHasBeenShown] = useState(false)
  // `open` is read on every render, not just the first: a caller that opens the tooltip
  // programmatically has revealed it just as surely as a hover, and must not be left without data.
  const isLoadEnabled = shouldLoadEagerly || hasBeenShown || open === true

  const { data: analysesPage } = useGetAnalyses({
    page: { number: 0, size: 1 },
    filters: {
      scanIds: [scanId],
      states: ['completed'],
    },
    enabled: isLoadEnabled,
  })
  const completedAnalysis = analysesPage?._embedded.items[0]

  const sortedLog = [...(completedAnalysis?.log ?? [])].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  const firstQueuedEntry = sortedLog.find(l => l.state === 'queued')
  const completedAt = completedAnalysis?.current_state.timestamp
  const duration = firstQueuedEntry && completedAt ? formatDuration(firstQueuedEntry.timestamp, completedAt) : undefined

  const latestAnalysisProps =
    completedAt !== undefined ? { completedAt, duration, findingsCount: completedAnalysis?.findings_count } : undefined

  const { latestCancellationTimestamp } = useGetScanCancellation({ scanId, enabled: isLoadEnabled })

  return (
    <ScanDateTimeTooltipPure
      importedAt={importedAt}
      latestAnalysis={latestAnalysisProps}
      cancelledAt={latestCancellationTimestamp}
      toolName={toolName}
      instanceName={instanceName}
      onShow={() => setHasBeenShown(true)}
      open={open}
    />
  )
}

export type ScanDateTimeTooltipPureProps = {
  importedAt: string
  latestAnalysis?: {
    completedAt: string
    duration?: string
    findingsCount?: number
  }
  cancelledAt?: string
  toolName?: string
  instanceName?: string
  /** Fired when the trigger is hovered, so the impure wrapper can load its data on demand. */
  onShow?: () => unknown
  open?: boolean
}

export const ScanDateTimeTooltipPure: React.FC<ScanDateTimeTooltipPureProps> = ({
  importedAt,
  latestAnalysis,
  cancelledAt,
  toolName,
  instanceName,
  onShow,
  open,
}) => (
  <TooltipWithHover
    trigger={
      // A button, not a div: this tooltip is the only place the producing instance is named, so it
      // has to be reachable by keyboard. Radix opens on focus as well as hover, and `onFocus` loads
      // the data for the same reason `onPointerEnter` does.
      <button
        type="button"
        className={styles.tooltipTrigger}
        data-testid="scan-date-time-tooltip-trigger"
        aria-label="Scan timing details"
        onPointerEnter={onShow}
        onFocus={onShow}
        // In the scans list this trigger sits inside the row's navigating `<Link>`; without this a
        // click on the icon reaches the anchor and navigates instead of showing the tooltip.
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          onShow?.()
        }}
      >
        <InfoIcon size={15} weight="regular" />
      </button>
    }
    open={open}
    side="right"
    align={latestAnalysis || cancelledAt ? 'start' : 'center'}
  >
    <div className={analysisLogStyles.tooltipContainer}>
      {instanceName && (
        <div className={styles.tooltipInstanceHeader}>
          {/* The tool name is dropped when it only repeats the instance — a system singleton named
              after its tool otherwise reads "Black Duck Polaris SAST Black Duck Polaris". Substring
              rather than equality, because the two tables disagree by a suffix. */}
          {toolName && !toolName.includes(instanceName) && (
            <>
              <span className={styles.tooltipInstanceToolName}>{toolName}</span>{' '}
            </>
          )}
          <span className={styles.tooltipInstanceName}>{instanceName}</span>
        </div>
      )}
      <div className={analysisLogStyles.logEntry.historical}>
        <div className={analysisLogStyles.logEntryLabel}>Scan imported</div>
        <div className={analysisLogStyles.logEntryTimestamp}>
          {formatDate({ date: importedAt, type: 'mm/dd/yy-time-with-zone' })}
        </div>
      </div>
      {latestAnalysis && (
        <div className={analysisLogStyles.logEntry.latest}>
          <div className={analysisLogStyles.logEntryLabel}>
            {typeof latestAnalysis.findingsCount === 'number'
              ? `Latest analysis (${latestAnalysis.findingsCount} findings)`
              : 'Latest analysis'}
          </div>
          <div className={analysisLogStyles.logEntryTimestamp}>
            {`Completed: ${formatDate({ date: latestAnalysis.completedAt, type: 'mm/dd/yy-time-with-zone' })}`}
          </div>
          {latestAnalysis.duration && (
            <div className={analysisLogStyles.logEntryTimestamp}>{`Duration: ${latestAnalysis.duration}`}</div>
          )}
        </div>
      )}
      {cancelledAt && (
        <div className={analysisLogStyles.logEntry.historical}>
          <div className={analysisLogStyles.logEntryLabel}>Analysis cancelled</div>
          <div className={analysisLogStyles.logEntryTimestamp}>
            {formatDate({ date: cancelledAt, type: 'mm/dd/yy-time-with-zone' })}
          </div>
        </div>
      )}
    </div>
  </TooltipWithHover>
)

type SonarApiWarningProps = {
  detector: string
  totalFindings: number
}

/**
 * Displays a warning when Sonar API results may be truncated.
 *
 * The Sonar API has a hard limit of 10,000 findings per request and does not
 * indicate when results are truncated. When we receive exactly 10,000 findings,
 * there's a high probability the actual count is higher and was silently truncated.
 * If the count is over 10,000, it means multiple requests were made and we have
 * the complete data, so no warning is needed.
 */
export const SonarApiWarning: React.FC<SonarApiWarningProps> = ({ detector, totalFindings }) => {
  const shouldShowWarning = detector === 'sonar' && totalFindings === 10000

  if (!shouldShowWarning) {
    return null
  }

  return (
    <TooltipWithHover
      trigger={
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
          <span className={typography.paragraph} style={{ color: mappedColors.secondary.haze }}>
            Findings total may be incomplete
          </span>
          <QuestionIcon className={styles.questionMarkIcon} size={16} weight="light" />
        </div>
      }
      side="left"
      maxWidth={300}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <span className={typography.extraSmallBold}>Sonar limits findings from its API to 10,000</span>
        <span className={typography.extraSmallItalic}>
          Because this scan reports the maximum number of findings permitted, there is reason to believe additional
          findings may exist.
        </span>
      </div>
    </TooltipWithHover>
  )
}

const ScanInputsExpiredWarning: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
    <span className={typography.paragraph} style={{ color: mappedColors.secondary.haze }}>
      Scan inputs expired
    </span>
    <TooltipWithHover
      trigger={
        <QuestionIcon className={styles.questionMarkIcon} size={16} weight="light" style={{ cursor: 'pointer' }} />
      }
      side="left"
      maxWidth={300}
    >
      <span className={typography.extraSmall}>Scan inputs have expired. Run a new scan to reanalyze.</span>
    </TooltipWithHover>
  </div>
)

// #endregion
